/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/userRightsSettings.scss';

import React, {
    ForwardedRef,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import Invitation, {InvitationFields} from "../../../../models/invitationModel";
import AccountUser, {AccountUserFields} from "../../../../models/accountUserModel";
import {ActivateHandle, UserRight, UserRightItem, UserRole} from "../../../../util/interfaces";
import {AppContext} from "../../../misc/appContext";
import {CloseButton, ResponseData, showDialog, showSnackbar, stopPropagation} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../../util/utils";
import {onShowConfirmSnackbar} from "../../../../util/renderUtil";
import {trackPlatformAction} from "../../../../util/analytics";
import {emailExists} from "../../../../util/requestSender";
import classNames from 'classnames';
import {AccountUserSettings, NewInvitationForm} from "./userRightsSettings";
import {DEFAULT_ROLE_RIGHTS, getDefaultRoleRights} from "../../../../util/accessUtil";
import PageChangePermission from "../../../misc/pageChangePermission";
import {requestPageChangePermission} from "../../../../util/pageChanger";

interface Props {
    onSuccess: VoidFunction;
}

enum FormType {NEW_INVITATION, EXISTING_INVITATION, ACCOUNT_USER}

function UserRightsController(props: Props, ref: ForwardedRef<ActivateHandle<Invitation | AccountUser>>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const [obj, setObj] = useState<Invitation | AccountUser>(new Invitation());
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);
    const [emailAlreadyAdded, setEmailAlreadyAdded] = useState<boolean>(false);
    const [formType, title]: [FormType, string] = useMemo(() => {
        if (obj instanceof AccountUser) return [FormType.ACCOUNT_USER, 'Rechten instellen'];
        else if (!obj.id) return [FormType.NEW_INVITATION, 'Gebruiker toevoegen'];
        return [FormType.EXISTING_INVITATION, 'Uitnodiging wijzigen'];
    }, [obj]);
    const outerRef = useRef<HTMLDivElement>(null);
    const hasChanged = useRef(false);

    function onActivate(obj?: Invitation | AccountUser) {
        obj = obj?.clone() ?? new Invitation({
            account_id: account?.id,
            rights: getDefaultRoleRights(UserRole.EMPLOYEE)
        });
        setObj(obj);
        setActive(true);
        hasChanged.current = false;
    }

    function onClose() {
        requestPageChangePermission(() => {
            setActive(false);
            setLoading(false);
        });
    }

    function onEnterEmail() {
        if (!account) return;
        emailExists({
            data: {email: obj.email, account_id: account.id, check_account_access: true},
            callback: (data: ResponseData) => {
                setLoading(false);
                setEmailAlreadyAdded(data.has_account_access ?? false);
                const _obj = obj.clone();
                if (_obj.userRole)
                    _obj.rights = getDefaultRoleRights(_obj.userRole);
                setObj(_obj);
            }
        });
    }

    function onChangeRightGroup(rightItem: UserRightItem) {
        return function(toggled: boolean) {
            if (!rightItem.children) return;
            hasChanged.current = true;
            const _obj = obj.clone();
            for (const item of rightItem.children) {
                const right: UserRight = _obj.findRight(item.key) ?? {...item, allow: toggled};
                right.allow = toggled;
                _obj.setRight(right);
            }
            setObj(_obj);
        }
    }

    function onChangeRight(right: UserRight) {
        return function(toggled: boolean) {
            hasChanged.current = true;
            right.allow = toggled;
            setObj(obj.clone().setRight(right));
        }
    }

    function onChangeObject(key: keyof InvitationFields & keyof AccountUserFields) {
        return function(value: string) {
            hasChanged.current = true;
            setObj(obj.clone().setField(key, value));
        }
    }

    function onChangeRole(userRole: UserRole) {
        hasChanged.current = true;
        const _obj = obj.clone().setField('user_role', userRole);
        const defaultRights = DEFAULT_ROLE_RIGHTS[userRole];
        _obj.rights = [];
        if (defaultRights)
            for (const key of defaultRights) _obj.setRightValue(key, true);
        setObj(_obj);
    }

    function onSubmit() {
        setLoading(true);
        // TODO Check for role and email
        // Check for new invitation
        if (obj instanceof Invitation && !obj.id) return _onInviteUser();
        const entity = obj instanceof Invitation ? 'uitnodiging is' : 'rechten zijn';
        obj.update((obj: Invitation | AccountUser, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                hasChanged.current = false;
                setObj(obj);
                showSnackbar(`De ${entity} bijgewerkt`);
                props.onSuccess();
                if (obj instanceof AccountUser) onAccountUserSuccess(obj);
            } else {
                showDialog(createErrorMessage(data.message ?? `Het bijwerken van de ${entity} is niet gelukt.`),
                    'Bijwerken mislukt');
            }
        });
    }

    function onAccountUserSuccess(obj: AccountUser) {
        if (obj.userId !== context.userContext.user?.id || !account) return;
        const _account = account.clone();
        _account.userRights = obj.rights;
        _account.userRole = obj.userRole;
        context.onChangeContext({account: _account});
    }

    function _onInviteUser() {
        setLoading(true);
        (obj as Invitation).invite((invitation: Invitation, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                hasChanged.current = false;
                trackPlatformAction('account.users', 'invite_user');
                if (data.invitation) {
                    setObj(invitation);
                    onShowConfirmSnackbar('De uitnodiging is verzonden');
                } else onShowConfirmSnackbar(data.message!);
                props.onSuccess();
                onClose();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het uitnodigen van de gebruiker is niet gelukt.'),
                    'Uitnodigen mislukt');
        });
    }

    function transitionListener() {
        if (active) outerRef.current?.removeEventListener('transitionend', transitionListener);
        else setObj(new Invitation());
        if (formType === FormType.NEW_INVITATION)
            document.getElementById('invitation-mail-input')?.focus();
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    useEffect(() => {
        window.setTimeout(() => {
            if (active) outerRef.current?.addEventListener('transitionend', transitionListener);
            else outerRef.current?.removeEventListener('transitionend', transitionListener);
        });

        return function() {
            outerRef.current?.removeEventListener('transitionend', transitionListener);
        }
    }, [active]);

    return (
        <>
            <div className={classNames('user-right-settings', active && 'active')} onMouseDown={onClose} ref={outerRef}>
                <div className='inner-container' onMouseDown={stopPropagation}>
                    <div className='header-container'>
                        <label className='header-title'>{title}</label>
                        <CloseButton onClick={onClose} className='btn-close'/>
                    </div>
                    {formType === FormType.NEW_INVITATION &&
                        <NewInvitationForm onChangeObject={onChangeObject} onEnterEmail={onEnterEmail}
                                           obj={obj as Invitation} onSubmit={onSubmit} onChangeRight={onChangeRight}
                                           onChangeRightGroup={onChangeRightGroup} onChangeRole={onChangeRole}
                                           onClose={onClose} loading={loading} active={active}
                                           emailAlreadyAdded={emailAlreadyAdded}/>}
                    {formType === FormType.ACCOUNT_USER &&
                        <AccountUserSettings onSubmit={onSubmit} onChangeRight={onChangeRight}
                                             onChangeRightGroup={onChangeRightGroup} onChangeObject={onChangeObject}
                                             onChangeRole={onChangeRole} onClose={onClose} obj={obj} loading={loading}
                                             active={active}/>}
                </div>
            </div>
            <PageChangePermission changed={hasChanged.current}/>
        </>
    )
}

export default forwardRef<ActivateHandle, Props>(UserRightsController);